import { oids } from "@/configs/Settings";
import { normalizeCpf } from "src-next/shared/utils/cpf";
import type { MessagingPatient } from "../types";
import { fhirClients } from "src-next/shared/api/fhirClient";

/**
 * patientService (mock)
 *
 * Responsabilidade:
 * - IO/integração (mock). Deve ser substituído por chamadas reais quando existir
 *   um service de pacientes no domínio correto.
 * - Manter uma API simples (search/create) para suportar o Dialog de envio.
 */

export type CreatePatientInput = {
  fullName: string;
  birthDate: string;
  phone?: string;
};

const SIMULATED_PATIENTS: MessagingPatient[] = [
  {
    id: "p-ana",
    fullName: "Ana Silva",
    birthDate: "1992-04-21",
    phone: "+5511999990001",
  },
  {
    id: "p-carlos",
    fullName: "Carlos Mendes",
    birthDate: "1988-09-02",
    phone: "+5511999990002",
  },
  {
    id: "p-beatriz",
    fullName: "Beatriz Santos",
    birthDate: "1996-12-10",
    phone: "+5511999990003",
  },
];

type SearchPatientsParams = {
  /**
   * Mantido por compatibilidade com a UI atual (Autocomplete).
   * Por enquanto NÃO aplicamos filtro adicional no servidor, conforme diretriz:
   * a busca com `locations` já retorna a lista completa de pacientes.
   */
  term?: string;
  /**
   * ID da organização atual.
   *
   * Usado para buscar `Location?organization=...` e então resolver os actors
   * para a busca de pacientes no scheduling.
   */
  organizationId?: string;
};

function pickPreferredPatientNameText(patient: fhir5.Patient): string {
  const names = patient.name ?? [];
  const official = names.find((name) => name.use === "official")?.text?.trim();
  if (official) return official;
  const first = names[0]?.text?.trim();
  return first ?? "";
}

function pickFirstPatientPhone(patient: fhir5.Patient): string | undefined {
  const telecom = patient.telecom ?? [];
  const phone = telecom.find((item) => item.system === "phone")?.value?.trim();
  return phone || undefined;
}

/**
 * Extrai CPF do recurso Patient (identifier com system OID de CPF).
 */
function pickPatientCpf(patient: fhir5.Patient): string | undefined {
  const cpfOid = oids.cpf;
  for (const identifier of patient.identifier ?? []) {
    const system = identifier.system ?? "";
    const value = identifier.value?.trim();
    if (!value) continue;
    if (system.includes(cpfOid) || system.endsWith(cpfOid)) {
      const digits = normalizeCpf(value);
      return digits.length === 11 ? digits : undefined;
    }
  }
  return undefined;
}

function toLocationActorReference(locationId: string): string {
  // `_has:Appointment:patient:actor` é Reference: preferimos formato `Location/{id}`.
  return `${locationId}`;
}

function mapPatientEntryToMessagingPatient(
  entry: fhir5.BundleEntry,
): MessagingPatient | null {
  const resource = entry.resource as fhir5.Patient | undefined;
  if (!resource || resource.resourceType !== "Patient") return null;
  if (!resource.id) return null;

  return {
    id: resource.id,
    fullName: pickPreferredPatientNameText(resource),
    birthDate: resource.birthDate,
    phone: pickFirstPatientPhone(resource),
    cpf: pickPatientCpf(resource),
  };
}

async function search(
  params: SearchPatientsParams,
): Promise<MessagingPatient[]> {
  const organizationId = String(params.organizationId ?? "").trim();

  // Sem organizationId, não conseguimos resolver `Location?organization=...`
  // e portanto não é seguro buscar pacientes sem o filtro por locations.
  if (!organizationId) {
    return [];
  }

  const organizationReference = `${oids.cnpj}-${organizationId}`;

  const locationsBundle = await fhirClients.workflow.search<fhir5.Location>(
    "Location",
    {
      organization: organizationReference,
      _count: 10000,
    },
  );

  const locationIds =
    locationsBundle.entry
      ?.map((entry) => (entry.resource as fhir5.Location | undefined)?.id)
      ?.filter((id): id is string => Boolean(id)) ?? [];

  if (locationIds.length === 0) {
    return [];
  }

  const locationActorsCsv = locationIds.map(toLocationActorReference).join(",");

  const patientsBundle = await fhirClients.scheduling.search<fhir5.Patient>(
    "Patient",
    {
      _count: 10000,
      "_has:Appointment:patient:actor": locationActorsCsv,
    },
  );

  const patients =
    patientsBundle.entry
      ?.map(mapPatientEntryToMessagingPatient)
      ?.filter((patient): patient is MessagingPatient => Boolean(patient)) ??
    [];

  return patients;
}

async function create(input: CreatePatientInput): Promise<MessagingPatient> {
  await new Promise<void>((resolve) => setTimeout(resolve, 450));

  // Mock: apenas retorna uma entidade com ID temporário.
  return {
    id: `temp-${Date.now()}`,
    fullName: input.fullName,
    birthDate: input.birthDate,
    phone: input.phone,
  };
}

export const patientService = {
  search,
  create,
};
